import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiUpload } from "react-icons/fi";
import darkThemeLogo from "../../assets/images/darkthemelogo.svg";
import { careersJobs } from "../../data/careersJobs";
import "./CareerApply.css";
import { applyCareerJob, getCareerJobByKey } from "../../api/careers";

const trim = (value) => String(value ?? "").trim();

const EDUCATION_LEVEL_KEYS = ["highSchool", "diploma", "bachelorDegree", "doctorate", "otherEducation", "educationLevel"];

const mapEducationPayload = (educationLevel, otherEducation) => {
  const base = { highSchool: "", diploma: "", bachelorDegree: "", doctorate: "", otherEducation: "" };
  switch (educationLevel) {
    case "highSchool":
      base.highSchool = "High School";
      break;
    case "bachelor":
      base.bachelorDegree = "Bachelor";
      break;
    case "phd":
      base.doctorate = "PhD";
      break;
    case "other":
      base.otherEducation = trim(otherEducation) || "Other";
      break;
    default:
      break;
  }
  return base;
};

const CareerApply = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [apiJob, setApiJob] = useState(undefined);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [applySuccessModalOpen, setApplySuccessModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    fullName: "",
    location: "",
    phoneNumber: "",
    email: "",
    city: "",
    nationality: "",
    yearsOfExperience: "",
    age: "",
    appliedRole: "",
    dateOfBirth: "",
    gender: "",
    source: "",
    educationLevel: "",
    highSchool: "",
    diploma: "",
    bachelorDegree: "",
    doctorate: "",
    otherEducation: "",
    languagesSpoken: "",
    softwareTools: "",
    totalExperienceYears: "",
    fieldOfExperience: "",
    experienceInAppliedPosition: "",
    currentlyEmployed: "",
    noticePeriod: "",
    salaryExpectation: "",
    salaryCurrency: "IQD",
  });

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const data = await getCareerJobByKey(jobId);
        if (isMounted) setApiJob(data || null);
      } catch {
        if (isMounted) setApiJob(null);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [jobId]);

  const fallbackJob = careersJobs.find((item) => item.id === jobId);
  const job = apiJob === undefined ? fallbackJob : (apiJob || fallbackJob);

  useEffect(() => {
    if (!job?.title) return;
    setForm((prev) => (trim(prev.appliedRole) ? prev : { ...prev, appliedRole: job.title }));
  }, [job?.title]);

  useEffect(() => {
    if (!applySuccessModalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [applySuccessModalOpen]);

  const progressWidth = useMemo(() => {
    if (step === 1) return "33.33%";
    if (step === 2) return "66.66%";
    return "100%";
  }, [step]);

  const validateStep1 = () => {
    if (!trim(form.fullName)) return "Please enter your full name.";
    if (!trim(form.location)) return "Please enter your city / location.";
    if (!trim(form.phoneNumber)) return "Please enter your phone number.";
    if (!trim(form.email)) return "Please enter your email.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trim(form.email))) return "Please enter a valid email address.";
    if (!trim(form.city)) return "Please enter your city.";
    if (!trim(form.nationality)) return "Please enter your nationality.";
    if (!trim(form.yearsOfExperience)) return "Please enter your years of experience.";
    if (!trim(form.appliedRole)) return "Please enter the role you are applying for.";
    if (!trim(form.dateOfBirth)) return "Please enter your date of birth.";
    if (!trim(form.gender)) return "Please enter your gender.";
    return null;
  };

  const validateStep2 = () => {
    if (!trim(form.source)) return "Please tell us where you heard about this job.";
    if (!trim(form.educationLevel)) return "Please select your education background.";
    if (form.educationLevel === "other" && !trim(form.otherEducation)) return "Please specify your education (Other).";
    return null;
  };

  const validateStep3 = () => {
    if (!trim(form.languagesSpoken)) return "Please enter languages spoken.";
    if (!trim(form.softwareTools)) return "Please enter software / tools.";
    if (!trim(form.totalExperienceYears)) return "Please enter total years of experience.";
    if (!trim(form.fieldOfExperience)) return "Please enter your field of experience.";
    if (!trim(form.experienceInAppliedPosition)) return "Please enter experience in the applied position.";
    const employed = trim(form.currentlyEmployed).toLowerCase();
    if (!employed || (employed !== "true" && employed !== "false")) {
      return "Please select whether you are currently employed.";
    }
    if (!trim(form.noticePeriod)) return "Please enter your notice period.";
    if (!trim(form.salaryExpectation)) return "Please enter your salary expectation.";
    if (!files.length) return "Please upload at least one file (e.g. CV).";
    return null;
  };

  const goToNextStep = (fromStep) => {
    setErrorMessage("");
    if (fromStep === 1) {
      const err = validateStep1();
      if (err) {
        setErrorMessage(err);
        return;
      }
      setStep(2);
      return;
    }
    if (fromStep === 2) {
      const err = validateStep2();
      if (err) {
        setErrorMessage(err);
        return;
      }
      setStep(3);
    }
  };

  if (apiJob === undefined && !fallbackJob) {
    return null;
  }

  if (!job) {
    return <Navigate to="/careers" replace />;
  }

  const handleApply = async () => {
    setErrorMessage("");
    const step3Err = validateStep3();
    if (step3Err) {
      setErrorMessage(step3Err);
      return;
    }
    if (!trim(form.fullName) || !trim(form.email) || !trim(form.phoneNumber)) {
      setErrorMessage("Full name, email and phone number are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = new FormData();
      const educationFields = mapEducationPayload(form.educationLevel, form.otherEducation);
      Object.entries(form).forEach(([key, value]) => {
        if (EDUCATION_LEVEL_KEYS.includes(key)) return;
        if (value === null || value === undefined || String(value).trim() === "") return;
        if (key === "currentlyEmployed") {
          const normalized = String(value).toLowerCase().trim();
          if (normalized === "true" || normalized === "false") {
            payload.append(key, normalized);
          }
          return;
        }
        payload.append(key, String(value).trim());
      });
      Object.entries(educationFields).forEach(([key, value]) => {
        if (trim(value)) payload.append(key, value);
      });
      files.forEach((file) => payload.append("files", file));
      await applyCareerJob({ jobId: job.id, payload });
      setStep(1);
      setForm({
        fullName: "",
        location: "",
        phoneNumber: "",
        email: "",
        city: "",
        nationality: "",
        yearsOfExperience: "",
        age: "",
        appliedRole: job.title || "",
        dateOfBirth: "",
        gender: "",
        source: "",
        educationLevel: "",
        highSchool: "",
        diploma: "",
        bachelorDegree: "",
        doctorate: "",
        otherEducation: "",
        languagesSpoken: "",
        softwareTools: "",
        totalExperienceYears: "",
        fieldOfExperience: "",
        experienceInAppliedPosition: "",
        currentlyEmployed: "",
        noticePeriod: "",
        salaryExpectation: "",
        salaryCurrency: "IQD",
      });
      setFiles([]);
      setApplySuccessModalOpen(true);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Failed to submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="careerApply">
      {applySuccessModalOpen ? (
        <div className="careerApply-modalOverlay">
          <div
            className="careerApply-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="careerApply-success-heading"
            aria-describedby="careerApply-success-desc"
          >
            <h2 id="careerApply-success-heading" className="careerApply-modalTitle">
              Thank you for your application!
            </h2>
            <p id="careerApply-success-desc" className="careerApply-modalText">
              Our HR team will review the provided information.
            </p>
            <div className="careerApply-modalActions">
              <button type="button" className="careerApply-modalBtn careerApply-modalBtn--secondary" onClick={() => navigate("/")}>
                Back to home page
              </button>
              <button type="button" className="careerApply-modalBtn" onClick={() => navigate("/careers")}>
                Back to careers
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="careerApply-container">
        <img src={darkThemeLogo} alt="IRQGYP" className="careerApply-logo" />

        <p className="careerApply-stepText">Next step: Position applying for</p>
        <div className="careerApply-progressTrack" aria-hidden>
          <span style={{ width: progressWidth }} />
        </div>

        {step > 1 && (
          <button
            type="button"
            className="careerApply-backStep"
            onClick={() => {
              setErrorMessage("");
              setStep((prev) => Math.max(1, prev - 1));
            }}
            aria-label="Go to previous step"
          >
            <FiArrowLeft />
            <span>Previous step</span>
          </button>
        )}

        {step === 1 && (
          <section className="careerApply-step">
            <p className="careerApply-subtitle">Applying for</p>
            <h1 className="careerApply-title">{job.title}</h1>

            <h2 className="careerApply-sectionTitle">Personal information</h2>

            <div className="careerApply-gridTwo">
              <input placeholder="Your full name" value={form.fullName} onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))} />
              <input placeholder="City / Location" value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} />
              <input placeholder="Phone number" value={form.phoneNumber} onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))} />
              <input placeholder="Your email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
              <input placeholder="City" value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} />
              <input placeholder="Nationality" value={form.nationality} onChange={(e) => setForm((p) => ({ ...p, nationality: e.target.value }))} />
            </div>

            <input className="careerApply-fullInput" placeholder="Years of experience" value={form.yearsOfExperience} onChange={(e) => setForm((p) => ({ ...p, yearsOfExperience: e.target.value }))} />
            <input className="careerApply-fullInput" placeholder="Current age (optional)" value={form.age} onChange={(e) => setForm((p) => ({ ...p, age: e.target.value }))} />
            <input className="careerApply-fullInput" placeholder="Role you're applying for" value={form.appliedRole} onChange={(e) => setForm((p) => ({ ...p, appliedRole: e.target.value }))} />
            <label className="careerApply-fieldLabel" htmlFor="careerApply-dob">
              Date of birth
            </label>
            <input
              id="careerApply-dob"
              className="careerApply-fullInput"
              type="date"
              value={form.dateOfBirth}
              onChange={(e) => setForm((p) => ({ ...p, dateOfBirth: e.target.value }))}
            />
            <label className="careerApply-fieldLabel" htmlFor="careerApply-gender">
              Gender
            </label>
            <select
              id="careerApply-gender"
              className="careerApply-fullInput"
              value={form.gender}
              onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}
              aria-label="Gender"
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>

            {errorMessage ? <p className="careerApply-subtitle" style={{ color: "#b42318" }}>{errorMessage}</p> : null}

            <button
              type="button"
              className="careerApply-nextBtn"
              onClick={() => goToNextStep(1)}
            >
              Next
            </button>
          </section>
        )}

        {step === 2 && (
          <section className="careerApply-step">
            <h2 className="careerApply-sectionTitle">Position Applied For</h2>

            <input className="careerApply-fullInput" placeholder="Job title" value={job.title || ""} readOnly />
            <input className="careerApply-fullInput" placeholder="Source (Where did you hear about this job?)" value={form.source} onChange={(e) => setForm((p) => ({ ...p, source: e.target.value }))} />

            <h2 className="careerApply-sectionTitle withGap">Education Background</h2>
            <label className="careerApply-fieldLabel" htmlFor="careerApply-education">
              Highest level completed
            </label>
            <select
              id="careerApply-education"
              className="careerApply-fullInput"
              value={form.educationLevel}
              onChange={(e) => {
                const level = e.target.value;
                setForm((p) => ({
                  ...p,
                  educationLevel: level,
                  otherEducation: level === "other" ? p.otherEducation : "",
                }));
              }}
              aria-label="Education background"
            >
              <option value="">Select education</option>
              <option value="highSchool">High School</option>
              <option value="bachelor">Bachelor</option>
              <option value="phd">PhD</option>
              <option value="other">Other</option>
            </select>
            {form.educationLevel === "other" ? (
              <input
                className="careerApply-fullInput"
                placeholder="Please specify"
                value={form.otherEducation}
                onChange={(e) => setForm((p) => ({ ...p, otherEducation: e.target.value }))}
                aria-label="Education details (other)"
              />
            ) : null}

            {errorMessage ? <p className="careerApply-subtitle" style={{ color: "#b42318" }}>{errorMessage}</p> : null}

            <button
              type="button"
              className="careerApply-nextBtn"
              onClick={() => goToNextStep(2)}
            >
              Next
            </button>
          </section>
        )}

        {step === 3 && (
          <section className="careerApply-step">
            <h2 className="careerApply-sectionTitle">Skills & Competencies</h2>
            <input className="careerApply-fullInput" placeholder="Languages spoken" value={form.languagesSpoken} onChange={(e) => setForm((p) => ({ ...p, languagesSpoken: e.target.value }))} />
            <input className="careerApply-fullInput" placeholder="Software/Tools" value={form.softwareTools} onChange={(e) => setForm((p) => ({ ...p, softwareTools: e.target.value }))} />

            <h2 className="careerApply-sectionTitle withGap">Work experience</h2>
            <input className="careerApply-fullInput" placeholder="Total Years of experience" value={form.totalExperienceYears} onChange={(e) => setForm((p) => ({ ...p, totalExperienceYears: e.target.value }))} />
            <input className="careerApply-fullInput" placeholder="Field of Experience" value={form.fieldOfExperience} onChange={(e) => setForm((p) => ({ ...p, fieldOfExperience: e.target.value }))} />
            <input className="careerApply-fullInput" placeholder="Total Years of experience in the applied position" value={form.experienceInAppliedPosition} onChange={(e) => setForm((p) => ({ ...p, experienceInAppliedPosition: e.target.value }))} />
            <label className="careerApply-fieldLabel" htmlFor="careerApply-employed">
              Are you currently employed?
            </label>
            <select
              id="careerApply-employed"
              className="careerApply-fullInput"
              value={form.currentlyEmployed}
              onChange={(e) => setForm((p) => ({ ...p, currentlyEmployed: e.target.value }))}
              aria-label="Currently employed"
            >
              <option value="">Select</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
            <input className="careerApply-fullInput" placeholder="Notice period" value={form.noticePeriod} onChange={(e) => setForm((p) => ({ ...p, noticePeriod: e.target.value }))} />
            <div className="careerApply-salary">
              <input placeholder="Salary Expectation" value={form.salaryExpectation} onChange={(e) => setForm((p) => ({ ...p, salaryExpectation: e.target.value }))} />
              <span>{form.salaryCurrency || "IQD"}</span>
            </div>

            <label className="careerApply-uploadBox">
              <p>Upload files (CV / cover letter / certificates)</p>
              <span>
                Click to browse <FiUpload />
              </span>
              <input type="file" multiple style={{ display: "none" }} onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </label>
            {files.length ? <p className="careerApply-subtitle">{files.length} file(s) selected</p> : null}
            {errorMessage ? <p className="careerApply-subtitle" style={{ color: "#b42318" }}>{errorMessage}</p> : null}

            <button type="button" className="careerApply-nextBtn" onClick={handleApply} disabled={submitting}>
              {submitting ? "Submitting..." : "Apply"}
            </button>
          </section>
        )}
      </div>
    </main>
  );
};

export default CareerApply;

